import {
  addDays,
  differenceInCalendarDays,
  format,
  isValid,
  parse,
  parseISO,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import {
  formatTorontoEventDate,
  formatTorontoEventTime,
  getCanonicalEventTimestamp,
} from '../../../utils/eventTruth';
import {
  getReportingProxyHeaders,
  getReportingProxyUrl,
} from '../../../utils/reportingClient';

const WEEK_STARTS_ON = 1;
export const WEEK_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
export const DEFAULT_DASHBOARD_REPORTING_API = 'https://maxpark-firebase-sync-worker.onrender.com';

const RECENT_ACTIVITY_LIMIT = 5;
const DASHBOARD_ACTIVITY_CACHE_MS = 30000;

let dashboardActivityCache = null;
let dashboardKpiCache = null;

const DATE_TIME_FORMATS = [
  'yyyy/MM/dd HH:mm:ss',
  'yyyy/MM/dd HH:mm',
  'yyyy-MM-dd HH:mm:ss',
  'yyyy-MM-dd HH:mm',
  'yyyy/MM/dd',
  'yyyy-MM-dd',
];

const hasValue = value => value !== undefined && value !== null && value !== '';

const cleanUrl = value => String(value || '').trim().replace(/\/+$/, '');

const getReportingApiOrigin = configuredOrigin => cleanUrl(configuredOrigin);

const getNowIso = now => {
  const parsedNow = now instanceof Date ? now : new Date(now);
  return isValid(parsedNow) ? parsedNow.toISOString() : new Date().toISOString();
};

export const getDashboardActivityReportUrl = (options = {}) => {
  const origin = getReportingApiOrigin(options.reportingApi);
  const params = new URLSearchParams();

  if (options.now) {
    params.set('now', getNowIso(options.now));
  }

  if (!origin) return getReportingProxyUrl('dashboard-activity', params);
  const query = params.toString();
  return `${origin}/reports/dashboard-activity${query ? `?${query}` : ''}`;
};

export const getDashboardKpisReportUrl = (options = {}) => {
  const origin = getReportingApiOrigin(options.reportingApi);
  const params = new URLSearchParams();

  if (options.now) {
    params.set('now', getNowIso(options.now));
  }

  if (options.threshold) {
    params.set('threshold', String(options.threshold));
  }

  if (!origin) return getReportingProxyUrl('dashboard-kpis', params);
  const query = params.toString();
  return `${origin}/reports/dashboard-kpis${query ? `?${query}` : ''}`;
};

const parseDateValue = value => {
  if (!hasValue(value)) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return isValid(date) ? date : null;
  }

  const stringValue = String(value).trim();

  if (!stringValue) {
    return null;
  }

  const isoDate = parseISO(stringValue);

  if (isValid(isoDate)) {
    return isoDate;
  }

  for (const dateFormat of DATE_TIME_FORMATS) {
    const parsedDate = parse(stringValue, dateFormat, new Date());

    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }

  const fallbackDate = new Date(stringValue);
  return isValid(fallbackDate) ? fallbackDate : null;
};

const parseTicketIssuedDate = ticket => {
  const datetime = parseDateValue(ticket.datetime);

  if (datetime) {
    return datetime;
  }

  const date = ticket.date || '';
  const time = ticket.time || '00:00:00';
  return parseDateValue(`${date} ${time}`);
};

const parsePaymentDate = payment => {
  if (!payment || typeof payment !== 'object' || payment.paid !== true) {
    return null;
  }

  const paymentDate = parseDateValue(payment.paymentdate);

  if (paymentDate) {
    if (typeof payment.paymentdate === 'string' && !payment.paymentdate.includes('T') && hasValue(payment.paymenttime)) {
      const combinedDate = parseDateValue(`${payment.paymentdate} ${payment.paymenttime}`);
      return combinedDate || paymentDate;
    }

    return paymentDate;
  }

  return parseDateValue(payment.paymenttime);
};

const parseModifiedDate = ticket => parseDateValue(ticket.modified);

const mergePayment = (existingPayment = {}, incomingPayment = {}) => {
  const merged = { ...existingPayment };

  Object.keys(incomingPayment || {}).forEach(key => {
    const value = incomingPayment[key];

    if (key === 'paid') {
      merged.paid = existingPayment.paid === true || value === true;
      return;
    }

    if (hasValue(value) || !hasValue(merged[key])) {
      merged[key] = value;
    }
  });

  return merged;
};

const mergeTicket = (existingTicket, incomingTicket) => {
  if (!existingTicket) {
    return incomingTicket;
  }

  const merged = { ...existingTicket };

  Object.keys(incomingTicket).forEach(key => {
    if (key === 'payment') {
      merged.payment = mergePayment(existingTicket.payment, incomingTicket.payment);
      return;
    }

    const incomingValue = incomingTicket[key];

    if (hasValue(incomingValue) || !hasValue(merged[key])) {
      merged[key] = incomingValue;
    }
  });

  return merged;
};

const mergeTicketsById = tickets => {
  const ticketsById = new Map();

  tickets.forEach(ticket => {
    if (!ticket || !ticket.ticketid) {
      return;
    }

    ticketsById.set(ticket.ticketid, mergeTicket(ticketsById.get(ticket.ticketid), ticket));
  });

  return Array.from(ticketsById.values());
};

const toActivity = ({ ticket, type, statusLabel, activityDate }) => {
  const ticketNumber = ticket.ticketid || '';
  const clientName = ticket.clientname || ticket.contractname || '';

  return {
    id: `${type}-${ticketNumber}-${activityDate.getTime()}`,
    type,
    statusLabel,
    ticketNumber,
    clientName,
    timestamp: activityDate.getTime(),
    date: format(activityDate, 'yyyy-MM-dd'),
    time: format(activityDate, 'HH:mm'),
  };
};

export const normalizeAvisActivity = ticket => {
  if (!ticket) {
    return [];
  }

  const activities = [];
  const issuedDate = parseTicketIssuedDate(ticket);
  const paymentDate = parsePaymentDate(ticket.payment);
  const modifiedDate = parseModifiedDate(ticket);

  if (issuedDate) {
    activities.push(toActivity({
      ticket,
      type: 'Nouveau avis',
      statusLabel: ticket.status || '\u00c9mis',
      activityDate: issuedDate,
    }));
  }

  if (paymentDate) {
    activities.push(toActivity({
      ticket,
      type: 'Avis pay\u00e9',
      statusLabel: 'Pay\u00e9',
      activityDate: paymentDate,
    }));
  }

  if (modifiedDate) {
    activities.push(toActivity({
      ticket,
      type: 'Avis modifi\u00e9',
      statusLabel: ticket.status || 'Modifi\u00e9',
      activityDate: modifiedDate,
    }));
  }

  return activities;
};

const sortActivitiesNewestFirst = activities => activities.sort((left, right) => right.timestamp - left.timestamp);

const getRecentActivitiesFromTickets = tickets => sortActivitiesNewestFirst(
  mergeTicketsById(tickets).flatMap(normalizeAvisActivity)
).slice(0, RECENT_ACTIVITY_LIMIT);

const getWeekStart = now => startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON });

const getTicketDayIndex = (ticket, weekStart) => {
  const issuedDate = parseTicketIssuedDate(ticket);

  if (!issuedDate) {
    return -1;
  }

  return differenceInCalendarDays(startOfDay(issuedDate), startOfDay(weekStart));
};

const getWeekTicketCounts = (tickets, weekStart) => {
  const counts = Array(7).fill(0);

  tickets.forEach(ticket => {
    const dayIndex = getTicketDayIndex(ticket, weekStart);

    if (dayIndex >= 0 && dayIndex < counts.length) {
      counts[dayIndex] += 1;
    }
  });

  return counts;
};

export const getCurrentWeekTicketCounts = (tickets, now = new Date()) => getWeekTicketCounts(
  tickets,
  getWeekStart(now)
);

export const getPreviousWeekTicketCounts = (tickets, now = new Date()) => getWeekTicketCounts(
  tickets,
  addDays(getWeekStart(now), -7)
);

const normalizeCount = value => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const calculatePercentageChange = (currentWeekTotal, previousWeekTotal) => {
  if (previousWeekTotal === 0) {
    return currentWeekTotal === 0 ? 0 : 100;
  }

  return ((currentWeekTotal - previousWeekTotal) / previousWeekTotal) * 100;
};

export const formatDailyIssuedTicketTrend = (todayTotal, yesterdayTotal) => {
  const percentageChange = calculatePercentageChange(todayTotal, yesterdayTotal);

  if (yesterdayTotal === 0 && todayTotal > 0) {
    return {
      label: 'Nouveau vs hier',
      trend: 'positive',
      direction: 'up',
    };
  }

  const trend = todayTotal > yesterdayTotal
    ? 'positive'
    : todayTotal < yesterdayTotal
      ? 'negative'
      : 'neutral';
  const direction = todayTotal > yesterdayTotal
    ? 'up'
    : todayTotal < yesterdayTotal
      ? 'down'
      : 'neutral';
  const sign = percentageChange > 0 ? '+' : '';

  return {
    label: `${sign}${percentageChange.toFixed(2)}% vs hier`,
    trend,
    direction,
  };
};

export const formatPreviousPeriodTrend = (
  currentTotal,
  previousTotal,
  periodLabel = 'periode precedente',
  options = {}
) => {
  const current = normalizeCount(currentTotal);
  const previous = normalizeCount(previousTotal);
  const percentageChange = calculatePercentageChange(current, previous);
  const direction = current > previous
    ? 'up'
    : current < previous
      ? 'down'
      : 'neutral';

  const getTrend = () => {
    if (direction === 'neutral') {
      return 'neutral';
    }

    if (options.lowerIsBetter) {
      return direction === 'down' ? 'positive' : 'negative';
    }

    return direction === 'up' ? 'positive' : 'negative';
  };

  if (previous === 0 && current > 0) {
    return {
      label: `Nouveau vs ${periodLabel}`,
      trend: getTrend(),
      direction,
    };
  }

  const sign = percentageChange > 0 ? '+' : '';

  return {
    label: `${sign}${percentageChange.toFixed(2)}% vs ${periodLabel}`,
    trend: getTrend(),
    direction,
  };
};

export const formatDashboardKpiNumber = value => new Intl.NumberFormat('fr-CA').format(normalizeCount(value));

export const formatDashboardCurrency = value => new Intl.NumberFormat('fr-CA', {
  currency: 'CAD',
  maximumFractionDigits: 0,
  style: 'currency',
}).format(normalizeCount(value));

export const formatReportingFreshnessLabel = (freshness = {}) => {
  const latestReportingAt = parseDateValue(freshness.latestReportingAt);

  if (!latestReportingAt) {
    return 'Aucune sync';
  }

  const ageMinutes = normalizeCount(freshness.ageMinutes);
  const ageHours = Math.max(1, Math.round(ageMinutes / 60));

  if (freshness.status === 'idle') {
    return `Dernier evenement il y a ${ageHours} h`;
  }

  if (freshness.status === 'sync_delayed') {
    return `Sync en retard depuis ${ageHours} h`;
  }

  if (freshness.status === 'email_stale' || freshness.emailStatus === 'stale') {
    const emailAgeMinutes = freshness.emailAgeMinutes === null || freshness.emailAgeMinutes === undefined
      ? ageMinutes
      : normalizeCount(freshness.emailAgeMinutes);
    const emailAgeHours = Math.max(1, Math.round(emailAgeMinutes / 60));
    return `Courriels en retard depuis ${emailAgeHours} h`;
  }

  if (ageMinutes < 60) {
    return `Mis a jour ${format(latestReportingAt, 'HH:mm')}`;
  }

  return `Il y a ${ageHours} h`;
};

export const formatEmailSyncWarningLabel = (freshness = {}) => {
  if (!freshness.hasEmailSyncIssue) {
    return '';
  }

  const rawAgeMinutes = freshness.emailAgeMinutes === null || freshness.emailAgeMinutes === undefined
    ? freshness.ageMinutes
    : freshness.emailAgeMinutes;

  if (rawAgeMinutes === null || rawAgeMinutes === undefined || rawAgeMinutes === '') {
    return 'Aucune synchronisation courriel recente. Les KPI paiements et contestations peuvent etre incomplets.';
  }

  const ageMinutes = normalizeCount(rawAgeMinutes);
  const ageLabel = ageMinutes < 60
    ? `${ageMinutes} min`
    : `${Math.max(1, Math.round(ageMinutes / 60))} h`;

  return `Derniere synchronisation courriel il y a ${ageLabel}. Les KPI paiements et contestations peuvent etre incomplets.`;
};

const getTimestamp = () => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
);

const getDashboardActivityCacheKey = (now, options = {}) => [
  format(getWeekStart(now), 'yyyy-MM-dd'),
  format(startOfDay(now), 'yyyy-MM-dd'),
  getReportingApiOrigin(options.reportingApi),
].join(':');

const getDashboardKpiCacheKey = (now, options = {}) => [
  format(startOfDay(now), 'yyyy-MM-dd'),
  getReportingApiOrigin(options.reportingApi),
  options.threshold || '',
].join(':');

const getTicketCountForDay = (tickets, day) => {
  const normalizedDay = startOfDay(day);

  return tickets.reduce((total, ticket) => {
    const issuedDate = parseTicketIssuedDate(ticket);

    if (!issuedDate) {
      return total;
    }

    return differenceInCalendarDays(startOfDay(issuedDate), normalizedDay) === 0
      ? total + 1
      : total;
  }, 0);
};

export const getDailyIssuedTicketKpi = (tickets, now = new Date()) => {
  const today = startOfDay(now);
  const yesterday = addDays(today, -1);
  const todayTotal = getTicketCountForDay(tickets, today);
  const yesterdayTotal = getTicketCountForDay(tickets, yesterday);

  return {
    todayTotal,
    yesterdayTotal,
    percentageChange: calculatePercentageChange(todayTotal, yesterdayTotal),
  };
};

const normalizeDashboardKpiReport = (report = {}, fallbackLoadDurationMs = 0) => {
  if (!report || !['issuedTickets', 'vehicleAlerts', 'contestations', 'paymentConfirmations', 'freshness']
    .every(key => report[key] && typeof report[key] === 'object' && !Array.isArray(report[key]))) {
    throw new Error('Le rapport des indicateurs est incomplet.');
  }
  const issuedTickets = report.issuedTickets || {};
  const vehicleAlerts = report.vehicleAlerts || {};
  const contestations = report.contestations || {};
  const paymentConfirmations = report.paymentConfirmations || {};
  const freshness = report.freshness || {};

  return {
    issuedTickets: {
      todayTotal: normalizeCount(issuedTickets.todayTotal),
      yesterdayTotal: normalizeCount(issuedTickets.yesterdayTotal),
      percentageChange: normalizeCount(issuedTickets.percentageChange),
      last24hTotal: normalizeCount(issuedTickets.last24hTotal),
      previous24hTotal: normalizeCount(issuedTickets.previous24hTotal),
      last24hPercentageChange: normalizeCount(issuedTickets.last24hPercentageChange),
      currentWeekTotal: normalizeCount(issuedTickets.currentWeekTotal),
      previousWeekTotal: normalizeCount(issuedTickets.previousWeekTotal),
      currentSevenDayTotal: normalizeCount(
        hasValue(issuedTickets.currentSevenDayTotal)
          ? issuedTickets.currentSevenDayTotal
          : issuedTickets.currentWeekTotal
      ),
      previousSevenDayTotal: normalizeCount(
        hasValue(issuedTickets.previousSevenDayTotal)
          ? issuedTickets.previousSevenDayTotal
          : issuedTickets.previousWeekTotal
      ),
      weeklyPercentageChange: normalizeCount(issuedTickets.weeklyPercentageChange),
      sevenDayPercentageChange: normalizeCount(issuedTickets.sevenDayPercentageChange),
    },
    vehicleAlerts: {
      threshold: normalizeCount(vehicleAlerts.threshold || 5),
      totalVehicles: normalizeCount(vehicleAlerts.totalVehicles),
      totalEntries: normalizeCount(vehicleAlerts.totalEntries),
      maxNoticeCount: normalizeCount(vehicleAlerts.maxNoticeCount),
      vehiclesOver10: normalizeCount(vehicleAlerts.vehiclesOver10),
      vehiclesOver25: normalizeCount(vehicleAlerts.vehiclesOver25),
      vehiclesOver50: normalizeCount(vehicleAlerts.vehiclesOver50),
    },
    contestations: {
      todayTotal: normalizeCount(contestations.todayTotal),
      yesterdayTotal: normalizeCount(contestations.yesterdayTotal),
      percentageChange: normalizeCount(contestations.percentageChange),
      matchedNoticeCount: normalizeCount(contestations.matchedNoticeCount),
      last24hTotal: normalizeCount(contestations.last24hTotal),
      previous24hTotal: normalizeCount(contestations.previous24hTotal),
      last24hPercentageChange: normalizeCount(contestations.last24hPercentageChange),
      last24hMatchedNoticeCount: normalizeCount(contestations.last24hMatchedNoticeCount),
    },
    paymentConfirmations: {
      todayTotal: normalizeCount(paymentConfirmations.todayTotal),
      yesterdayTotal: normalizeCount(paymentConfirmations.yesterdayTotal),
      percentageChange: normalizeCount(paymentConfirmations.percentageChange),
      todayAmount: normalizeCount(paymentConfirmations.todayAmount),
      matchedNoticeCount: normalizeCount(paymentConfirmations.matchedNoticeCount),
      missingAmountCount: normalizeCount(paymentConfirmations.missingAmountCount),
      noticeAmountFallbackCount: normalizeCount(paymentConfirmations.noticeAmountFallbackCount),
      defaultAmountFallbackCount: normalizeCount(paymentConfirmations.defaultAmountFallbackCount),
      last24hTotal: normalizeCount(paymentConfirmations.last24hTotal),
      previous24hTotal: normalizeCount(paymentConfirmations.previous24hTotal),
      last24hPercentageChange: normalizeCount(paymentConfirmations.last24hPercentageChange),
      last24hAmount: normalizeCount(paymentConfirmations.last24hAmount),
      previous24hAmount: normalizeCount(paymentConfirmations.previous24hAmount),
      last24hAmountPercentageChange: normalizeCount(paymentConfirmations.last24hAmountPercentageChange),
      last24hMatchedNoticeCount: normalizeCount(paymentConfirmations.last24hMatchedNoticeCount),
      last24hMissingAmountCount: normalizeCount(paymentConfirmations.last24hMissingAmountCount),
      last24hNoticeAmountFallbackCount: normalizeCount(paymentConfirmations.last24hNoticeAmountFallbackCount),
      last24hDefaultAmountFallbackCount: normalizeCount(paymentConfirmations.last24hDefaultAmountFallbackCount),
    },
    freshness: {
      latestReportingAt: freshness.latestReportingAt || '',
      ageMinutes: freshness.ageMinutes === null || freshness.ageMinutes === undefined
        ? null
        : normalizeCount(freshness.ageMinutes),
      status: freshness.status || 'unknown',
      latestNoticeSyncAt: freshness.latestNoticeSyncAt || '',
      latestEmailSeenAt: freshness.latestEmailSeenAt || '',
      latestEmailSyncAt: freshness.latestEmailSyncAt || '',
      latestEmailSyncFailedAt: freshness.latestEmailSyncFailedAt || '',
      latestFirebaseProcessedAt: freshness.latestFirebaseProcessedAt || '',
      latestFailedSyncAt: freshness.latestFailedSyncAt || '',
      oldestProcessingSyncAt: freshness.oldestProcessingSyncAt || '',
      emailAgeMinutes: freshness.emailAgeMinutes === null || freshness.emailAgeMinutes === undefined
        ? null
        : normalizeCount(freshness.emailAgeMinutes),
      emailStatus: freshness.emailStatus || 'unknown',
      hasEmailSyncIssue: Boolean(freshness.hasEmailSyncIssue),
      hasSyncIssue: Boolean(freshness.hasSyncIssue),
    },
    loadedSources: Array.isArray(report.loadedSources) && report.loadedSources.length
      ? report.loadedSources
      : ['render-postgres'],
    errors: Array.isArray(report.errors) ? report.errors : [],
    firebasePaths: Array.isArray(report.firebasePaths) ? report.firebasePaths : [],
    firebaseQueryStrategy: report.firebaseQueryStrategy || 'render-postgres dashboard kpi report',
    dataSource: report.dataSource || 'render-postgres',
    loadDurationMs: normalizeCount(report.loadDurationMs || fallbackLoadDurationMs),
    loadedAt: report.loadedAt || new Date().toISOString(),
  };
};

const normalizeWeekCounts = counts => {
  const normalized = Array(7).fill(0);

  if (Array.isArray(counts)) {
    counts.slice(0, 7).forEach((count, index) => {
      normalized[index] = normalizeCount(count);
    });
  }

  return normalized;
};

const normalizeActivity = (activity = {}, fallbackIndex = 0) => {
  const timestamp = getCanonicalEventTimestamp(activity);
  const canonicalDate = formatTorontoEventDate(timestamp);
  const canonicalTime = formatTorontoEventTime(timestamp);

  return {
    id: activity.id || `activity-${fallbackIndex}`,
    type: activity.type || 'Avis',
    statusLabel: activity.statusLabel || '',
    ticketNumber: activity.ticketNumber || activity.ticketid || '',
    clientName: activity.clientName || activity.contractname || activity.location || '',
    timestamp,
    date: canonicalDate || activity.date || '',
    time: canonicalTime || activity.time || '',
  };
};

const normalizeDashboardActivityReport = (report = {}, fallbackLoadDurationMs = 0) => {
  const activities = Array.isArray(report.activities)
    ? report.activities.map(normalizeActivity)
    : [];
  const currentWeekCounts = normalizeWeekCounts(report.currentWeekCounts);
  const previousWeekCounts = normalizeWeekCounts(report.previousWeekCounts);
  const currentWeekTotal = normalizeCount(
    hasValue(report.currentWeekTotal)
      ? report.currentWeekTotal
      : currentWeekCounts.reduce((total, count) => total + count, 0)
  );
  const previousWeekTotal = normalizeCount(
    hasValue(report.previousWeekTotal)
      ? report.previousWeekTotal
      : previousWeekCounts.reduce((total, count) => total + count, 0)
  );
  const currentSevenDayTotal = normalizeCount(hasValue(report.currentSevenDayTotal) ? report.currentSevenDayTotal : currentWeekTotal);
  const previousSevenDayTotal = normalizeCount(hasValue(report.previousSevenDayTotal) ? report.previousSevenDayTotal : previousWeekTotal);
  const todayTotal = normalizeCount(report.todayTotal);
  const yesterdayTotal = normalizeCount(report.yesterdayTotal);
  const last24hTotal = normalizeCount(report.last24hTotal);
  const previous24hTotal = normalizeCount(report.previous24hTotal);

  return {
    activities,
    currentWeekCounts,
    previousWeekCounts,
    currentWeekTotal,
    previousWeekTotal,
    currentSevenDayTotal,
    previousSevenDayTotal,
    percentageChange: normalizeCount(
      hasValue(report.percentageChange)
        ? report.percentageChange
        : calculatePercentageChange(currentSevenDayTotal, previousSevenDayTotal)
    ),
    sevenDayPercentageChange: normalizeCount(
      hasValue(report.sevenDayPercentageChange)
        ? report.sevenDayPercentageChange
        : calculatePercentageChange(currentSevenDayTotal, previousSevenDayTotal)
    ),
    todayTotal,
    yesterdayTotal,
    dailyPercentageChange: normalizeCount(
      hasValue(report.dailyPercentageChange)
        ? report.dailyPercentageChange
        : calculatePercentageChange(todayTotal, yesterdayTotal)
    ),
    last24hTotal,
    previous24hTotal,
    last24hPercentageChange: normalizeCount(
      hasValue(report.last24hPercentageChange)
        ? report.last24hPercentageChange
        : calculatePercentageChange(last24hTotal, previous24hTotal)
    ),
    weekLabels: Array.isArray(report.weekLabels) && report.weekLabels.length === 7
      ? report.weekLabels
      : WEEK_LABELS,
    loadedSources: Array.isArray(report.loadedSources) && report.loadedSources.length
      ? report.loadedSources
      : ['render-postgres'],
    errors: Array.isArray(report.errors) ? report.errors : [],
    firebasePaths: Array.isArray(report.firebasePaths) ? report.firebasePaths : [],
    firebaseQueryStrategy: report.firebaseQueryStrategy || 'render-postgres reporting query',
    dataSource: report.dataSource || 'render-postgres',
    loadDurationMs: normalizeCount(report.loadDurationMs || fallbackLoadDurationMs),
    loadedAt: report.loadedAt || new Date().toISOString(),
  };
};

export const loadDashboardActivityData = async (now = new Date(), options = {}) => {
  const cacheKey = getDashboardActivityCacheKey(now, options);
  const cachedAt = Date.now();

  if (
    !options.skipCache
    && dashboardActivityCache
    && dashboardActivityCache.key === cacheKey
    && cachedAt - dashboardActivityCache.cachedAt < DASHBOARD_ACTIVITY_CACHE_MS
  ) {
    return {
      ...dashboardActivityCache.data,
      cacheHit: true,
      loadDurationMs: 0,
    };
  }

  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);

  if (!fetchImpl) {
    throw new Error('Impossible de charger les activites: fetch non disponible.');
  }

  const loadStartedAt = getTimestamp();
  const headers = options.reportingApi
    ? { Accept: 'application/json' }
    : await getReportingProxyHeaders();
  const response = await fetchImpl(getDashboardActivityReportUrl({
    reportingApi: options.reportingApi,
    now,
  }), {
    headers,
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger les activites (${response.status}).`);
  }

  const report = await response.json();
  const data = normalizeDashboardActivityReport(
    report,
    Math.round(getTimestamp() - loadStartedAt)
  );

  dashboardActivityCache = {
    key: cacheKey,
    cachedAt: Date.now(),
    data,
  };

  return data;
};

export const loadDashboardKpiData = async (now = new Date(), options = {}) => {
  const cacheKey = getDashboardKpiCacheKey(now, options);
  const cachedAt = Date.now();

  if (
    !options.skipCache
    && dashboardKpiCache
    && dashboardKpiCache.key === cacheKey
    && cachedAt - dashboardKpiCache.cachedAt < DASHBOARD_ACTIVITY_CACHE_MS
  ) {
    return {
      ...dashboardKpiCache.data,
      cacheHit: true,
      loadDurationMs: 0,
    };
  }

  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);

  if (!fetchImpl) {
    throw new Error('Impossible de charger les KPI: fetch non disponible.');
  }

  const loadStartedAt = getTimestamp();
  const headers = options.reportingApi
    ? { Accept: 'application/json' }
    : await getReportingProxyHeaders();
  const response = await fetchImpl(getDashboardKpisReportUrl({
    reportingApi: options.reportingApi,
    now,
    threshold: options.threshold,
  }), {
    headers,
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger les KPI (${response.status}).`);
  }

  const report = await response.json();
  const data = normalizeDashboardKpiReport(
    report,
    Math.round(getTimestamp() - loadStartedAt)
  );

  dashboardKpiCache = {
    key: cacheKey,
    cachedAt: Date.now(),
    data,
  };

  return data;
};

export const loadDailyIssuedTicketKpiData = async (now = new Date(), options = {}) => {
  const data = await loadDashboardActivityData(now, options);

  return {
    todayTotal: data.todayTotal,
    yesterdayTotal: data.yesterdayTotal,
    percentageChange: data.dailyPercentageChange,
    loadedSources: data.loadedSources,
    firebasePaths: data.firebasePaths,
    firebaseQueryStrategy: data.firebaseQueryStrategy,
    dataSource: data.dataSource,
  };
};

export const fetchRecentAvisActivities = async (now = new Date(), options = {}) => {
  if (Array.isArray(options.sourceTickets)) {
    return getRecentActivitiesFromTickets(options.sourceTickets);
  }

  const data = await loadDashboardActivityData(now, options);
  return data.activities;
};

export const fetchCurrentWeekAvisCounts = async (now = new Date(), options = {}) => {
  if (Array.isArray(options.sourceTickets)) {
    return getCurrentWeekTicketCounts(options.sourceTickets, now);
  }

  const data = await loadDashboardActivityData(now, options);
  return data.currentWeekCounts;
};

export const fetchPreviousWeekAvisCounts = async (now = new Date(), options = {}) => {
  if (Array.isArray(options.sourceTickets)) {
    return getPreviousWeekTicketCounts(options.sourceTickets, now);
  }

  const data = await loadDashboardActivityData(now, options);
  return data.previousWeekCounts;
};
